/**
 * SurgeryPage.tsx
 *
 * The Surgery worklist — the V5 port of legacy `consults_full_list-test.php`.
 *
 * Page chrome is copied verbatim from the sibling list page
 * `src/pages/PlansPage.tsx` (its ListPageLayout > flex column > shrink-0 p-6
 * header block > min-h-0 flex-1 scroll body), so this page has no layout of its
 * own to get wrong.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, Download, Lock, Pencil } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Button, DataTable, EmptyState, Input, Modal, Select } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { PatientLink } from '@/components/ui/PatientLink';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { formatCurrency } from '@/lib/utils';
import { formatPacificDate } from '@/lib/facilityTimezone';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { BadgeVariant, SelectOption } from '@/types/common.types';
import type { FilterOption } from '@/types/filters.types';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import { usePlanFilterOptions } from '@/hooks/filters';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { toast } from '@/store/toastStore';
import { saveSurgeryRecord } from '@/services/surgeryRecord.service';
import { getErrorMessage } from '@/services/apiClient';
import * as surgerySvc from '@/services/surgery.service';
import type { SurgeryListItem, SurgerySearchType } from '@/services/surgery.service';
import {
  CONSULT_STATUS_OPTIONS,
  MEDICAL_CLEARANCE_OPTIONS,
  SURGERY_RETRY_OR_SUPPORT,
  SURGERY_SORT_KEYS,
  buildSurgeryFilterConfigs,
  buildSurgeryListParams,
  hasActiveSurgeryFilters,
  surgeryListView,
} from './surgeryShared';

const PAGE_SIZE = 25;

const SEARCH_TYPE_OPTIONS: SelectOption<SurgerySearchType>[] = [
  { value: 'name', label: 'Name' },
  { value: 'planId', label: 'Plan ID' },
  { value: 'patientId', label: 'Patient ID' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** The case's own page: the plan, opened on its Surgery section. */
function caseUrl(row: SurgeryListItem): string {
  return `/patients/${row.patientId}/plans/${row.planId}?section=surgery`;
}

/**
 * Legacy painted each row green / yellow / red from `$checklistOk`. The server
 * now sends a semantic status and the styling lives here.
 */
const ROW_STATUS_STYLES: Record<SurgeryListItem['rowStatus'], string> = {
  ready: 'bg-emerald-50',
  attention: 'bg-amber-50',
  blocked: 'bg-red-50',
  open: '',
};

function clearanceVariant(clearance: string): BadgeVariant {
  if (clearance === 'Cleared' || clearance === 'Not Needed') return 'success';
  if (clearance === 'Not Cleared' || clearance === 'Postponed') return 'error';
  if (clearance === '') return 'neutral';
  return 'warning';
}

function statusVariant(status: string): BadgeVariant {
  if (status === 'Approved') return 'success';
  if (status === 'Not Approved') return 'error';
  if (status === 'QBS Required') return 'warning';
  return 'info';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SurgeryPage() {
  const navigate = useNavigate();
  // What PatientLink records on click. The row opens the case rather than the
  // patient's chart, which PatientLink / usePatientNavigation cannot target, so
  // the row records the same recent-patients visit itself.
  const visitPatient = usePatientHistoryStore((s) => s.visit);

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildSurgeryFilterConfigs(doctorLabel, coordLabel),
    [doctorLabel, coordLabel],
  );

  const filterState = useLocalDashboardFilters(filterConfigs, 'surgery');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<SurgerySearchType>('name');
  // Legacy's table opens on column 0 descending (nav-test.js:2804-2805), i.e.
  // Procedure Date DESC. Opening ascending instead silently reorders the whole
  // worklist on first load.
  const [sort, setSort] = useState<string>(SURGERY_SORT_KEYS[0]);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  usePageContext('Surgery');

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'surgery',
    entityLabel: 'surgical cases',
  });

  // Doctor / coordinator / facility option lists are shared with the plans list.
  const planOptions = usePlanFilterOptions();

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      ...planOptions,
      'consult-status': CONSULT_STATUS_OPTIONS,
      'consult-clearance': MEDICAL_CLEARANCE_OPTIONS,
    }),
    [planOptions],
  );

  const params = useMemo(
    () =>
      buildSurgeryListParams(effectiveValues, filterState.negations, {
        page,
        perPage: PAGE_SIZE,
        sort,
        order,
        searchType,
      }),
    [effectiveValues, filterState.negations, page, sort, order, searchType],
  );

  // Reset to the first page whenever the result set changes underneath.
  const filterSignature = JSON.stringify({ ...params, page: 0 });
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  const { data, isLoading, isPlaceholderData, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['surgery-list', params],
    queryFn: () => surgerySvc.listSurgeryCases(params),
    placeholderData: (prev) => prev,
  });

  const view = surgeryListView({
    isError,
    error,
    hasActiveFilters: hasActiveSurgeryFilters(params),
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const doctorLocked = data?.meta?.doctorLocked ?? false;

  // Legacy edited this in a popup from the Note cell and capped it at 50 chars.
  const queryClient = useQueryClient();
  const [noteRow, setNoteRow] = useState<SurgeryListItem | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const saveNote = useMutation({
    mutationFn: ({ planId, note }: { planId: number; note: string }) =>
      saveSurgeryRecord(planId, { listNote: note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgery-list'] });
      setNoteRow(null);
    },
    onError: (err) =>
      toast.error('Could not save the note', getErrorMessage(err, SURGERY_RETRY_OR_SUPPORT)),
  });

  const openNote = (row: SurgeryListItem) => {
    setNoteRow(row);
    setNoteDraft(row.note ?? '');
  };

  /** Deep link to this case, for pasting into a message. */
  const copyLink = async (row: SurgeryListItem) => {
    const url = `${window.location.origin}${caseUrl(row)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      // The address bar here shows the worklist, not the case, so the only
      // place the user can copy this link by hand is the case itself.
      toast.error('Could not copy the link', 'Open the case and copy it from there.');
    }
  };

  // A doctor-user only ever sees their own cases, so the control would be a
  // no-op. The server enforces this regardless of what the client sends.
  const visibleFilterConfigs = useMemo(
    () => (doctorLocked ? filterConfigs.filter((c) => c.key !== 'doctor') : filterConfigs),
    [filterConfigs, doctorLocked],
  );

  // Both dates arrive as Pacific wall clocks. Procedure Date is the facility's
  // day (the chart tab shows the same time in the same zone); Last Update is a
  // staff action, so it reads in the viewer's zone like the tab's stamps.
  const userTz = useUserTimezone();

  const columns: DataTableColumn<SurgeryListItem>[] = useMemo(
    () => [
      {
        key: 'procedureDate',
        header: 'Procedure Date',
        sortable: true,
        render: (r) =>
          r.procedureDate ? (
            <span className="whitespace-nowrap text-xs">{formatPacificDate(r.procedureDate, r.facilityTimezone ?? '')}</span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        key: 'patientName',
        header: 'Patient',
        sortable: true,
        role: 'title',
        render: (r) => (
          <PatientLink
            patientId={r.patientId}
            patientName={r.patientName}
            className="font-medium text-blue-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.patientName}
          </PatientLink>
        ),
      },
      { key: 'procedures', header: 'Procedure(s)', sortable: true, truncate: true },
      { key: 'doctorName', header: doctorLabel, sortable: true, truncate: true },
      { key: 'coordinatorName', header: coordLabel, sortable: true, truncate: true },
      {
        key: 'labsReviewed',
        header: 'Labs Reviewed',
        sortable: true,
        render: (r) => <span className="text-xs">{r.labsReviewed || '—'}</span>,
      },
      {
        key: 'medicalClearance',
        header: 'Medical Clearance',
        sortable: true,
        render: (r) =>
          r.medicalClearance ? (
            <Badge variant={clearanceVariant(r.medicalClearance)} size="sm">
              {r.medicalClearance}
            </Badge>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        key: 'due',
        header: 'Due',
        sortable: true,
        align: 'right',
        render: (r) => <span className="text-xs">{formatCurrency(r.due)}</span>,
      },
      {
        key: 'consultStatus',
        header: 'Consult Status',
        sortable: true,
        render: (r) =>
          r.consultStatus ? (
            <Badge variant={statusVariant(r.consultStatus)} size="sm">
              {r.consultStatus}
            </Badge>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        key: 'note',
        header: 'Note',
        sortable: true,
        truncate: true,
        render: (r) => (
          <button
            type="button"
            className="flex items-center gap-1 text-left text-xs text-slate-700 hover:text-slate-900 hover:underline touch-target-h"
            title="Edit note"
            onClick={(e) => {
              e.stopPropagation();
              openNote(r);
            }}
          >
            {r.note ? r.note : <em className="text-slate-400">Edit note</em>}
            <Pencil className="h-3 w-3 shrink-0 opacity-60" />
          </button>
        ),
      },
      {
        key: 'lastUpdatedAt',
        header: 'Last Update',
        sortable: true,
        render: (r) =>
          r.lastUpdatedAt ? (
            <span className="flex flex-col items-start leading-tight whitespace-nowrap">
              <span className="text-xs">{formatPacificDate(r.lastUpdatedAt, userTz)}</span>
              {r.lastUpdatedBy ? (
                <span className="text-xs text-slate-400">{r.lastUpdatedBy}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-slate-400">Not updated</span>
          ),
      },
      {
        key: 'link',
        header: 'Link',
        width: '60px',
        align: 'center',
        label: 'Link',
        render: (r) => (
          <button
            type="button"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 touch-target-44"
            title="Copy a link to this case"
            aria-label="Copy a link to this case"
            onClick={(e) => {
              e.stopPropagation();
              void copyLink(r);
            }}
          >
            <Copy className="h-4 w-4 touch-target-icon" />
          </button>
        ),
      },
    ],
    // openNote and copyLink are stable for a render pass and only read row data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doctorLabel, coordLabel, userTz],
  );

  const handleSortChange = (key: string, dir: 'asc' | 'desc') => {
    setSort(key);
    setOrder(dir);
    setPage(1);
  };

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={visibleFilterConfigs}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="surgery"
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:overflow-x-hidden max-md:h-auto max-md:pb-16">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Surgery"
            actions={(
              <>
                {/* Legacy offered this beside the search box. It stays in the
                    header: FilterSidebar has no slot for a page's own control, and
                    a filter config would persist it and send it as a filter. */}
                <div className="flex items-center gap-2 text-sm">
                  <label htmlFor="surgery-search-by" className="text-slate-500">
                    Search by
                  </label>
                  <Select<SurgerySearchType>
                    id="surgery-search-by"
                    wrapperClassName="w-36"
                    options={SEARCH_TYPE_OPTIONS}
                    value={searchType}
                    onChange={(v) => {
                      setSearchType(v ?? 'name');
                      setPage(1);
                    }}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download className="h-4 w-4" />}
                  disabled={total === 0 || exporting}
                  loading={exporting}
                  onClick={() => startExport({ ...params })}
                >
                  Export
                </Button>
                {exportModal}
              </>
            )}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {view.kind === 'refused' ? (
            <EmptyState
              icon={<Lock className="h-8 w-8 text-slate-400" aria-hidden="true" />}
              title={view.title}
              description={view.message}
            />
          ) : view.kind === 'failed' ? (
            <EmptyState
              icon={<AlertTriangle className="h-8 w-8 text-slate-400" aria-hidden="true" />}
              title={view.title}
              description={view.message}
              action={{
                label: 'Retry',
                variant: 'secondary',
                loading: isFetching,
                onClick: () => void refetch(),
              }}
            />
          ) : (
          <DataTable<SurgeryListItem>
            data={rows}
            columns={columns}
            keyField="consultRequestId"
            loading={isLoading || isPlaceholderData}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            manualSort
            sortKey={sort}
            sortDir={order}
            onSortChange={handleSortChange}
            tableFixed
            className="[&>div]:overflow-x-hidden"
            rowClassName={(row) => ROW_STATUS_STYLES[row.rowStatus]}
            onRowClick={(row) => {
              visitPatient({ patientId: row.patientId, name: row.patientName });
              navigate(caseUrl(row));
            }}
            emptyTitle={view.emptyTitle}
            emptyDescription={view.emptyDescription}
          />
          )}
        </div>
      </div>

      <Modal
        isOpen={noteRow !== null}
        onClose={() => setNoteRow(null)}
        title="Edit Note"
        size="sm"
        footer={(
          <>
            <Button variant="secondary" size="sm" onClick={() => setNoteRow(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={saveNote.isPending}
              onClick={() =>
                noteRow && saveNote.mutate({ planId: noteRow.planId, note: noteDraft })
              }
            >
              Save
            </Button>
          </>
        )}
      >
        <Input
          label="Note"
          value={noteDraft}
          maxLength={50}
          autoFocus
          onChange={(e) => setNoteDraft(e.target.value)}
        />
      </Modal>
    </ListPageLayout>
  );
}
